import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Constrain, Stack, Text, ControlLink } from '@moda/om';
import { hrefFor } from '../../routers';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useOrder } from '../../hooks/useOrder';
import { useReturnSummary } from '../../hooks/useReturnSummary';
import { PageMeta } from '../../components/PageMeta';
import { Loading } from '../../components/Loading';
import { RefundSummary } from '../../components/PaymentSummary';
import {
  RequestReturnEligibleItems,
  RequestReturnNonEligibleItems,
  RequestReturnMethod,
  RequestReturnOptions,
  RequestReturnActions
} from '../../components/RequestReturn';
import { tc } from '../../lib/trackingContext';
import { useIsGuestUser } from '../../hooks/useIsGuestUser';
import { RequestReturnInProgressItems } from '../RequestReturn/RequestReturnInProgressItems';

import './RequestReturnForm.scss';

export const RequestReturnForm: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const isGuestUser = useIsGuestUser();
  const { order, loading: loadingOrder, error: orderError } = useOrder(orderNumber);
  const {
    returnSummary,
    loading: loadingReturnSummary,
    error: returnSummaryError
  } = useReturnSummary({
    orderNumber
  });

  if (orderError) throwQueryError(orderError);
  if (returnSummaryError) throwQueryError(returnSummaryError);

  useEffect(() => isGuestUser && tc.track('Guest Request Return Page Viewed'), [isGuestUser]);

  if (!order || !returnSummary || loadingOrder || loadingReturnSummary) return <Loading />;

  return (
    <>
      <PageMeta title="Request Return" />

      <Constrain className="RequestReturnForm">
        <Stack space={5}>
          <Stack space={3}>
            <Text treatment="h3" family="serif">
              Free Returns Service
            </Text>
            <Text color="cement">
              We are pleased to accept timely returns within 28 days from the date that the order
              was delivered to you for full-priced items, or within 14 days from the date that the
              order was delivered to you for discounted products or products purchased with a
              promotion. Unfortunately, original shipping charges are non-refundable. Click{' '}
              <ControlLink href="https://help.modaoperandi.com/hc/en-us/articles/211521863-Return-Policy">
                here
              </ControlLink>{' '}
              for more information on returns.
            </Text>
            <div className="RequestReturnForm__order-details">
              <Stack direction="horizontal" space={3}>
                <Text>Order Number</Text>
                <Text color="cement">
                  <ControlLink to={hrefFor.OrderDetailPage({ orderNumber })} underlined={false}>
                    #{orderNumber}
                  </ControlLink>
                </Text>
              </Stack>
              <Stack direction="horizontal" space={3}>
                <Text>Order Date</Text>
                <Text color="cement">
                  {format(new Date(order.attributes.createdAt), 'MMMM dd, yyyy')}
                </Text>
              </Stack>
            </div>
          </Stack>
          <div className="RequestReturnForm__main">
            <Stack className="RequestReturnForm__form" space={5}>
              <RequestReturnEligibleItems />

              <RequestReturnInProgressItems />

              <RequestReturnNonEligibleItems />

              <RequestReturnMethod />

              <RequestReturnOptions />
            </Stack>
            <div className="RequestReturnForm__summary">
              <div className="RequestReturnForm__summary-content">
                <RefundSummary orderNumber={orderNumber} />
              </div>
            </div>
          </div>
          <RequestReturnActions />
        </Stack>
      </Constrain>
    </>
  );
};
